import { alpha, useTheme, ButtonBase, LinearProgress, Stack, Typography } from '@mui/material'
import { ArrowRightIcon, CheckCircle2Icon } from 'lucide-react'

import type { TourProgress } from 'services/tourProgressApi'

import type { TourDef } from './tours'

/**
 * An action row, not a selectable card — clicking it immediately starts the tour
 * and there is no persistent selected state — so it takes the brand-border +
 * focus-ring hover treatment (working-conventions §34).
 */
export const TourRow = ({
  tour,
  onClick,
  progress,
}: {
  tour: TourDef
  onClick: () => void
  /** From `tourProgress`; `undefined` = untaken or not yet known. */
  progress?: TourProgress
}) => {
  const theme = useTheme()

  const totalSteps = tour.steps.length
  /*
    STARTED BUT NOT FINISHED — which is emphatically not the same as `in_progress`.

    `in_progress` is only the status WHILE the tour is running. The moment you leave
    one part-way — Esc, Skip, clicking away — it is recorded as `dismissed` with the
    step you reached. So keying the bar on `in_progress` made it very nearly
    unreachable: the library is closed while a tour runs, so by the time anyone can
    look at the row, the status has already become `dismissed`. That is exactly the
    reported "I click through some steps and the bar doesn't update" — the step was
    recording correctly (verified: 0 → 3 in the database); the bar was disappearing.

    The real question is "did they get somewhere and not finish", so ask that:
    anything not completed, that has moved past the first step. A tour dismissed AT
    step 0 gets no bar, correctly — declining the invitation is not partial progress.
  */
  const hasPartialProgress =
    progress !== undefined && progress.status !== 'completed' && progress.currentStep > 0
  // Clamped: a tour that loses steps in a later release would otherwise leave
  // someone stranded at "Step 9 of 6" with a bar past 100%.
  const stepIndex = Math.min(progress?.currentStep ?? 0, Math.max(totalSteps - 1, 0))
  const stepNumber = stepIndex + 1
  const pct = totalSteps > 0 ? Math.round((stepIndex / totalSteps) * 100) : 0

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        justifyContent: 'space-between',
        textAlign: 'left',
        gap: 3,
        px: 3,
        py: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
        bgcolor: 'background.paper',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover, &:focus-visible': {
          borderColor: theme.palette.brand[600],
          boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
        },
      }}
    >
      <Stack spacing={1} sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
          <Typography variant="body3" sx={{ fontWeight: 700 }}>
            {tour.label}
          </Typography>
          {/*
            Completed ONLY — not "seen". `dismissed` covers skipping on step 1 and
            dismissing the invitation without opening it, and badging either as
            done would credit someone for a tour they never took. That distinction
            is the whole reason `tourStatus` exists alongside `hasSeenTour`, and it
            is what any future "reward completion" scheme has to rest on: a badge
            that can be earned by pressing Skip is not a signal.
          */}
          {progress?.status === 'completed' && (
            // Brand purple, matching `TakeATourChip`'s clickable label color
            // (`brand[600]`) — not the generic MUI `success` green. This dialog's own
            // convention splits by ROLE, not by "is it a status color": brand[500] is
            // reserved for solid FILLS (the progress bar below, tint backgrounds),
            // while brand[600] is the shade used for status/interactive TEXT (the
            // chip's own label). This badge is text+icon, so it follows the chip.
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ color: theme.palette.brand[600], flexShrink: 0 }}
            >
              <CheckCircle2Icon strokeWidth={1.5} size={14} />
              <Typography variant="body4" component="span">
                Completed
              </Typography>
            </Stack>
          )}
        </Stack>
        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {tour.description}
        </Typography>

        {/*
          Part-way through: a bar plus "Step N of M".

          The bar and the count say different things and both earn their place — the
          bar is the glanceable "nearly there", the count is the precise answer to
          "how much is left", and a bar alone leaves someone guessing whether the
          remainder is one step or six.

          Shown for any STARTED-but-unfinished tour — see `hasPartialProgress`. An
          earlier version gated this on `in_progress` and so almost never rendered.

          `currentStep` is a zero-based index of where they stopped, so the number
          of steps BEHIND them is that index — displayed +1 as the human "step N".
        */}
        {hasPartialProgress && (
          <Stack spacing={1} sx={{ pt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.brand[500], 0.15),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  bgcolor: theme.palette.brand[500],
                },
              }}
            />
            <Typography variant="body4" color="text.secondary">
              Step {stepNumber} of {totalSteps}
            </Typography>
          </Stack>
        )}
      </Stack>
      <ArrowRightIcon
        strokeWidth={1}
        size={16}
        color={alpha(theme.palette.primary[500], 0.8)}
        style={{ flexShrink: 0 }}
      />
    </ButtonBase>
  )
}
